/** Office schedule: this week for every deputy, and a month calendar per deputy (supervisors can set days). */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { useAuth } from '../shared/auth.tsx';
import { hours, ScheduleCalendar } from '../shared/ScheduleCalendar.tsx';
import { Card, ErrorBox, Spinner, useLoad } from '../shared/ui.tsx';

const iso = (tz: string, d = new Date()) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const addDays = (s: string, n: number) => new Date(Date.parse(`${s}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const monthEnd = (m: string) => {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, mm, 0)).toISOString().slice(0, 10);
};

export function Schedule() {
  const { navigate } = useRouter();
  const { me } = useAuth();
  const today = iso(me!.agency.timezone);
  const weekStart = addDays(today, -new Date(`${today}T12:00:00Z`).getUTCDay());
  const week = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const [month, setMonth] = useState(today.slice(0, 7));
  const [deputy, setDeputy] = useState('');
  const team = useLoad(() => api.get<any>(`/api/schedule?from=${week[0]}&to=${week[6]}`), []);
  const one = useLoad(() => (deputy ? api.get<any>(`/api/schedule?from=${month}-01&to=${monthEnd(month)}&userId=${deputy}`) : Promise.resolve(null)), [deputy, month]);
  if (team.error) return <ErrorBox error={team.error} />;
  if (!team.data) return <Spinner />;
  const t = team.data;
  const cell = (uid: string, day: string) => t.schedule.find((e: any) => e.user_id === uid && e.day === day);
  return (
    <div className="stack">
      <div>
        <h1>Deputy schedule</h1>
        <p className="muted">What deputies have entered, and what supervisors have set. Build a stack uses these hours.</p>
      </div>
      <Card title="This week">
        <div className="table-wrap">
          <table className="table sched-week">
            <thead>
              <tr>
                <th>Deputy</th>
                {week.map((d) => (
                  <th key={d} className={d === today ? 'today' : ''}>
                    {new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC' })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.deputies.map((u: any) => (
                <tr key={u.id} className="clickable" onClick={() => setDeputy(u.id)}>
                  <td>
                    <strong>{u.display_name}</strong>
                  </td>
                  {week.map((d) => {
                    const e = cell(u.id, d);
                    return (
                      <td key={d} className={`${e ? `k-${e.kind}` : ''} ${e?.source === 'supervisor' ? 'sup' : ''}`}>
                        {e ? (e.kind === 'working' ? hours(e) : e.kind === 'flexible' ? 'Flexible' : 'Off') : <span className="muted">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="small muted">Click a deputy to see or set their month.</p>
      </Card>
      <Card title="Deputy calendar">
        <select value={deputy} onChange={(e) => setDeputy(e.target.value)} aria-label="Deputy">
          <option value="">Choose a deputy…</option>
          {t.deputies.map((u: any) => (
            <option key={u.id} value={u.id}>
              {u.display_name}
            </option>
          ))}
        </select>
        {deputy && one.data && (
          <ScheduleCalendar
            userId={deputy}
            month={month}
            today={today}
            entries={one.data.schedule}
            suggestions={one.data.suggestions}
            supervisor={t.canEdit}
            readOnly={!t.canEdit}
            onMonth={setMonth}
            onSaved={() => (one.reload(), team.reload())}
            court={one.data.court}
            onCourt={(id) => navigate(`/court/${id}`)}
          />
        )}
        {deputy && !t.canEdit && <p className="small muted">View only — supervisors can set days.</p>}
      </Card>
    </div>
  );
}
